// English translations. Keys mirror de.ts exactly.
const en = {
  app: {
    name: 'ewigbyte',
    tagline: 'Glass Archive Portal',
    adminTagline: 'Admin Console',
    skipToContent: 'Skip to content',
    logout: 'Log out',
    account: 'Account',
    tenant: 'Tenant',
    theme: 'Appearance',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'System',
    language: 'Language',
    languageGerman: 'German',
    languageEnglish: 'English',
    openMenu: 'Open navigation',
    closeMenu: 'Close navigation',
    roleUser: 'User',
    roleAdmin: 'Administrator',
  },
  nav: {
    dashboard: 'Dashboard',
    archives: 'Archives',
    upload: 'Upload',
    s3import: 'S3 Import',
    search: 'Search',
    versions: 'Versions',
    retention: 'Retention',
    retrieval: 'Retrieval',
    audit: 'Audit reports',
    billing: 'Billing',
    notifications: 'Notifications',
    sectionMain: 'Sections',
    sectionAdmin: 'Admin',
    adminOverview: 'Overview',
    adminCustomers: 'Customers',
    adminSubscriptions: 'Subscriptions',
    adminPlans: 'Subscription plans',
    adminFlags: 'Feature flags',
    adminBuckets: 'Buckets',
    adminBurns: 'Burns',
    adminBurnQueue: 'Approvals',
    adminUsers: 'Users',
    adminVerification: 'Verification',
  },
  common: {
    loading: 'Loading …',
    retry: 'Try again',
    cancel: 'Cancel',
    confirm: 'Confirm',
    close: 'Close',
    save: 'Save',
    create: 'Create',
    loadMore: 'Load more',
    all: 'All',
    none: 'None',
    name: 'Name',
    createdAt: 'Created',
    size: 'Size',
    objects: 'Objects',
    actions: 'Actions',
    details: 'Details',
    back: 'Back',
    search: 'Search',
    yes: 'Yes',
    no: 'No',
    unavailableTitle: 'This feature is being finalized',
    unavailableBody:
      'The underlying service is not available yet. As soon as it is ready, this section will appear automatically with real data — no sample data is ever shown.',
    errorTitle: 'Something went wrong',
    unauthorizedTitle: 'Sign-in required',
    unauthorizedBody: 'Your session has expired or you are not signed in.',
    login: 'Go to sign-in',
    emptyDefault: 'Nothing here yet.',
    bytes: 'bytes',
    copy: 'Copy',
    copied: 'Copied',
    optional: 'optional',
    required: 'required',
  },
  quota: {
    title: 'Purchased volume',
    purchased: 'Purchased',
    used: 'Burned to glass',
    remaining: 'Remaining',
    staged: 'Uploaded, not yet burned',
    hint: '{{percent}}% used across {{snapshots}} snapshot(s). {{staged}} is uploaded but not yet burned — staged data does not consume your volume.',
    noQuota: 'No purchased volume is agreed for this account. Only data burned to glass counts as used.',
    chart: {
      title: 'Storage usage',
      burned: 'burned to glass',
      staged: 'waiting to be burned',
      free: 'still free',
      ofPurchased: 'of purchased volume',
      empty: 'Nothing uploaded or burned yet.',
      overQuota: 'The purchased volume is exceeded by {{over}}.',
      committedHint: '{{committed}} is already committed — {{staged}} of it not yet burned.',
    },
  },
  retention: {
    KEEP_ALL: 'Keep all versions',
    KEEP_LATEST: 'Keep latest version only',
    KEEP_LAST_N: 'Keep last N versions',
    label: 'Retention policy',
    current: 'Current policy',
    change: 'Change policy',
    changeTitle: 'Change retention',
    warnTitle: 'Irreversible action',
    warnBody:
      'A stricter retention policy may permanently release older object versions for deletion. This action cannot be undone.',
    confirmChange: 'Change policy irreversibly',
    pickArchive: 'Choose archive',
    updated: 'Retention policy updated.',
  },
  dashboard: {
    title: 'Dashboard',
    welcome: 'Welcome back, {{name}}',
    subtitle: 'Your glass archive at a glance.',
    totalArchives: 'Archives',
    totalObjects: 'Objects',
    totalBytes: 'Stored',
    recentActivity: 'Recent activity',
    noActivity: 'No activity recorded yet.',
    quickCreate: 'New archive',
    viewAll: 'View all',
    yourArchives: 'Your archives',
  },
  archives: {
    title: 'Archives',
    subtitle: 'Manage your archives and their contents.',
    create: 'Create archive',
    createTitle: 'New archive',
    nameLabel: 'Archive name',
    namePlaceholder: 'e.g. Accounting 2026',
    empty: 'You have no archives yet. Create your first archive.',
    created: 'Archive “{{name}}” was created.',
    colObjects: 'Objects',
    colSize: 'Size',
    colRetention: 'Retention',
    deliverDmd: 'Deliver DMD',
    deliverDmdHint: 'Builds and downloads this archive’s DMD image (.dmd.tar.gz).',
    detailTitle: 'Archive details',
    delete: 'Delete',
    deleteConfirmTitle: 'Delete this archive?',
    deleteConfirmBody: 'This permanently deletes “{{name}}” and all its objects and versions. This cannot be undone.',
    deleted: 'Archive deleted.',
    sync: 'Sync from S3',
    syncHint: 'Re-pull the source bucket: new and changed objects are added; nothing is deleted.',
    synced: 'Sync complete — {{count}} objects processed.',
    syncedPartial: 'Sync only partial: {{count}} objects taken, {{skipped}} skipped. A single run transfers at most {{limit}} — run it again to continue.',
    syncFailed: 'Sync failed.',
    syncTitle: 'Synchronisation',
    syncRunning: 'Running…',
    syncCounting: 'Reading the source…',
    syncProgressLabel: 'Synchronisation progress',
    syncObjects: '{{done}} of {{total}} objects',
    syncSkipped: '{{count}} skipped',
    syncState: {
      completed: 'Completed',
      completed_partial: 'Partially completed',
      failed: 'Failed',
      running: 'Running…',
    },
  },
  snapshots: {
    requested: 'Burn request filed — it will be reviewed and approved.',
    alreadyRequested: 'This archive already has an open request.',
    requestTitle: 'Burn request',
    requestedAt: 'Requested {{when}} · {{volume}} · {{cost}}',
    requestNote: 'Note',
    requestState: {
      requested: 'Awaiting approval',
      approved: 'Approved',
      building: 'Image being built…',
      rejected: 'Rejected',
      failed: 'Failed',
    },
    confirmTitle: 'Burn this snapshot?',
    confirmBody: 'A sealed DMD image of “{{name}}” will be written to glass. This cannot be undone.',
    confirmVolume: 'Volume to burn',
    confirmCost: 'Estimated cost',
    confirmQuota: 'Volume remaining after',
    confirmNoPrice: 'No priced plan is assigned, so no cost can be quoted.',
    quote: 'est. {{cost}}',
    rateLimited: 'Daily write limit reached — the commit was refused. Try again after the limit resets.',
    blockedHint: 'A commit would exceed the daily write limit for this account.',
    budgetLeft: '{{left}} left today',
    budgetExceeded: 'Needs {{need}}, only {{left}} left today',
    title: 'Snapshots',
    subtitle: 'Sealed, burned DMD images of this archive — each one retrievable.',
    burn: 'Secure now',
    burnHint: 'Seals the archive and burns a snapshot (DMD) you can retrieve later.',
    burned: 'Snapshot v{{seq}} burned.',
    empty: 'No snapshots yet. Use “Secure now” or set up a scheduled commit.',
    colSeq: 'Version',
    colTrigger: 'Trigger',
    colDmdSize: 'DMD size',
    triggerManual: 'Manual',
    triggerScheduled: 'Scheduled',
    retrieve: 'Retrieve',
  },
  schedule: {
    title: 'Schedule',
    subtitle: 'Automate sync and commit (burn). Runs at ~02:00 UTC; monthly on the last day.',
    sync: 'Automatic sync',
    syncHint: 'Re-pull the S3 source on this cadence.',
    commit: 'Automatic commit (burn)',
    commitHint: 'Seal and burn a snapshot on this cadence.',
    save: 'Save schedule',
    saved: 'Schedule saved.',
    nextCommit: 'Next commit: {{when}}',
    nextSync: 'next sync: {{when}}',
    freq_off: 'Off',
    freq_daily: 'Daily',
    freq_weekly: 'Weekly',
    freq_monthly: 'Monthly',
    time: 'Time',
    tz: 'Europe/Berlin',
    weekday: 'Day of week',
    dayOfMonth: 'Day of month',
    domLast: 'Last day',
    domNth: 'Day {{n}}',
    wd_1: 'Monday',
    wd_2: 'Tuesday',
    wd_3: 'Wednesday',
    wd_4: 'Thursday',
    wd_5: 'Friday',
    wd_6: 'Saturday',
    wd_7: 'Sunday',
    objectsTitle: 'Objects',
    objectsEmpty: 'This archive has no objects yet.',
    colPath: 'Path',
    colVersions: 'Versions',
    colHash: 'SHA-256',
    identifier: 'Identifier',
  },
  upload: {
    queued: '{{count}} file queued',
    queued_other: '{{count}} files queued',
    quote: '{{size}} · est. {{cost}}',
    title: 'Upload',
    subtitle: 'Upload objects into an archive. Multiple files and retry-on-failure are supported.',
    pickArchive: 'Target archive',
    dropzone: 'Drag files here or choose them',
    dropzoneHint: 'Any number of files. Each file is uploaded individually with progress.',
    choose: 'Choose files',
    startAll: 'Upload all',
    clearDone: 'Clear finished',
    statusQueued: 'Queued',
    statusUploading: 'Uploading',
    statusDone: 'Done',
    statusError: 'Failed',
    retry: 'Retry',
    remove: 'Remove',
    abort: 'Cancel',
    noArchive: 'Please choose a target archive first.',
    needArchives: 'You need at least one archive before you can upload.',
    createFirst: 'Create archive',
    doneToast: '{{count}} file uploaded successfully.',
    doneToast_other: '{{count}} files uploaded successfully.',
  },
  s3import: {
    rate: '{{rate}} per GB written · min. {{min}} per file',
    title: 'S3 Import',
    subtitle: 'Import objects from an S3-compatible bucket.',
    sources: 'Known sources',
    noSources: 'No pre-configured sources.',
    endpoint: 'Endpoint',
    bucket: 'Bucket',
    prefix: 'Prefix',
    prefixPlaceholder: 'optional/path/',
    start: 'Start import',
    started: 'Import job {{id}} started.',
    endpointPlaceholder: 'https://s3.example.com',
    bucketPlaceholder: 'my-bucket',
    accessKey: 'Access key',
    accessKeyPlaceholder: 'AKIA… / access key',
    secretKey: 'Secret key',
    secretKeyPlaceholder: '••••••••',
    region: 'Region',
    credHint: 'Credentials are required for external buckets. Leave blank only for the platform’s own MinIO. They are used for this import and never stored.',
    doneTitle: 'Import complete',
    doneInto: 'Objects from “{{bucket}}” were imported into a new archive.',
    imported: 'Objects imported',
    openArchive: 'Open archive',
  },
  search: {
    title: 'Search',
    subtitle: 'Search across archive and object names.',
    placeholder: 'Search by name or path …',
    empty: 'No results for “{{q}}”.',
    prompt: 'Enter a search term to begin.',
    colArchive: 'Archive',
    colPath: 'Path',
    resultsCount: '{{count}} result',
    resultsCount_other: '{{count}} results',
  },
  versions: {
    title: 'Versions',
    subtitle: 'Inspect the version history of an object.',
    pickArchive: 'Archive',
    pickObject: 'Object',
    colVersion: 'Version',
    empty: 'No versions exist for this object.',
    prompt: 'Choose an archive and an object to see its versions.',
    noObjects: 'This archive has no objects.',
  },
  retrieval: {
    title: 'Retrieval',
    subtitle: 'Estimate media, duration and cost for retrieving objects.',
    pickArchive: 'Archive',
    pickObjects: 'Select objects',
    estimate: 'Request cost estimate',
    resultTitle: 'Estimate',
    media: 'Media required',
    days: 'Duration',
    daysValue: '{{count}} day',
    daysValue_other: '{{count}} days',
    eur: 'Estimated cost',
    spanned: 'Warning: at least one object spans multiple media (spanned object).',
    notSpanned: 'No object spans multiple media.',
    selectHint: 'Select at least one object.',
    noObjects: 'This archive has no objects.',
    deliver: 'Retrieve to S3 bucket',
    delivering: 'Provisioning bucket and copying …',
    deliverHint: 'Creates a dedicated S3 bucket, copies the selected objects into it, and returns time-limited download links.',
    deliveredTitle: 'Retrieval ready',
    bucket: 'Delivery bucket',
    endpoint: 'S3 endpoint',
    expires: 'Links valid for {{count}} hours',
    download: 'Download',
    openConsole: 'Open in S3',
  },
  audit: {
    title: 'Audit reports',
    subtitle: 'Traceable, immutable audit trail.',
    colSeq: 'No.',
    colTime: 'Time',
    colActor: 'Actor',
    colAction: 'Action',
    colResource: 'Resource',
    colResult: 'Result',
    empty: 'No audit events yet.',
  },
  billing: {
    rateCard: 'Your rates',
    rateWrite: 'Writing to glass',
    rateStorage: 'Storage',
    rateRead: 'Retrieval',
    rateFreeReadouts: 'Free retrievals (lifetime)',
    rateMinBillable: 'Minimum billable file size',
    rateMinBillableHint: 'Smaller files are charged at this size.',
    perGb: '{{price}} / GB',
    perGbYear: '{{price}} / GB / year',
    perReadout: '{{price}} / retrieval',
    noPlan: 'No priced plan is assigned to this account, so no rates can be shown.',
    title: 'Billing',
    subtitle: 'Media-centric billing estimate.',
    colPeriod: 'Period',
    colWritten: 'Media written',
    colRetrieved: 'Media retrieved',
    colEur: 'Amount',
    total: 'Total',
    empty: 'No billing data available.',
    storagePerMonth: 'Storage / month',
    pricedBy: 'Priced by plan',
  },
  notifications: {
    title: 'Notifications',
    subtitle: 'System messages and events.',
    empty: 'No notifications.',
    markAll: 'Mark all as read',
    unread: 'unread',
  },
  admin: {
    overview: {
      title: 'Overview',
      subtitle: 'Platform-wide customers, usage and breach alerts.',
      customers: 'Customers',
      alerts: 'Rate-limit alerts',
      alertsEmpty: 'No tenant is close to its rate limit.',
      stored: 'Stored',
      objects: 'Objects',
      scheduled: 'Scheduled buckets',
      snapshots: 'Snapshots',
      upcoming: 'Upcoming & overdue',
      noUpcoming: 'Nothing scheduled.',
      allOnTrack: 'All on track',
      overdueCount: '{{count}} overdue',
      colWhen: 'When',
      colWhat: 'What',
      colDate: 'Date',
      planMix: 'Subscriptions',
      recent: 'Recent activity',
      inMinutes: 'in {{count}} min',
      inHours: 'in {{count}} h',
      inDays: 'in {{count}} d',
      overdueBy: '{{rel}} ago',
      kind: { sync: 'Sync', commit: 'Burn / commit', verification: 'Verification' },
      colMetric: 'Metric',
      colUsage: 'Usage',
      colPercent: '% of limit',
    },
    burnQueue: {
      title: 'Approvals',
      subtitle: 'Customer burn requests. A DMD is built and written to glass only after approval.',
      pendingOnly: 'Open requests only',
      empty: 'No requests.',
      emptyPending: 'No open requests — nothing to do.',
      requested: 'Requested',
      volume: 'Volume',
      cost: 'Quote',
      status: 'Status',
      decision: 'Decision',
      approve: 'Approve',
      reject: 'Reject',
      approved: 'Approved — the image for "{{archive}}" is being built now.',
      decidedBy: 'by {{who}}',
      rejectTitle: 'Reject the request for "{{archive}}"',
      reason: 'Reason',
      reasonHint: 'The customer is shown this text.',
      reasonRequired: 'Please give a reason.',
      state: {
        requested: 'Awaiting approval',
        approved: 'Approved',
        building: 'Being built…',
        completed: 'Burned',
        rejected: 'Rejected',
        failed: 'Failed',
      },
    },
    burns: {
      title: 'Burns',
      subtitle: 'Every image burned to glass across all customers — with its digest as evidence.',
      searchPlaceholder: 'Customer, bucket or digest…',
      empty: 'Nothing has been burned yet.',
      when: 'When',
      customer: 'Customer',
      bucket: 'Bucket',
      seq: 'No.',
      objects: 'Objects',
      dmdSize: 'DMD size',
      digest: 'Digest',
      trigger: 'Trigger',
      by: 'By',
      triggerKind: { manual: 'Manual', scheduled: 'Scheduled' },
    },
    buckets: {
      create: 'New bucket',
      createTitle: 'New bucket',
      created: 'Bucket “{{name}}” created.',
      title: 'Buckets',
      subtitle: 'Every bucket across all customers, with its schedule and snapshots.',
      filterPlaceholder: 'Filter by bucket or customer …',
      scheduledOnly: 'Only scheduled',
      empty: 'No buckets found.',
      emptyScheduled: 'No bucket runs on a schedule.',
      colBucket: 'Bucket',
      colCustomer: 'Customer',
      colSync: 'Sync',
      colCommit: 'Burn / commit',
      colSnapshots: 'Snapshots',
      off: 'off',
    },
    users: {
      title: 'Users',
      subtitle: 'Identities that have acted on the platform, per customer.',
      sourceNote: 'Accounts live in Keycloak. This lists activity actually observed in the audit trail — dormant accounts do not appear here.',
      filterPlaceholder: 'Filter by user or customer …',
      empty: 'No activity recorded yet.',
      colUser: 'User',
      colActions: 'Actions',
      colLastAction: 'Last action',
      colLastSeen: 'Last seen',
      colFirstSeen: 'First seen',
      platformScope: 'Platform-wide',
    },
    override: {
      title: 'Plan & rates — {{name}}',
      hint: 'Every field shows the value that currently applies. Change only what was agreed; leave a field untouched and it keeps following the plan. Clear a field to remove an existing override.',
      needPlan: 'Choose a plan first.',
      group: { price: 'Prices', limit: 'Volumes & limits', service: 'Service attributes' },
    },
    impersonation: {
      banner: 'Viewing as “{{tenant}}” — this session is time-boxed and audit-logged.',
      exit: 'Exit',
    },
    flags: {
      title: 'Feature flags',
      subtitle: 'Which flag value applies to each customer, and where it comes from.',
      overriddenOnly: 'Only customers with an override',
      empty: 'No customers to show.',
      emptyOverridden: 'No customer has a flag override.',
      clear: 'Reset',
      clearHint: 'Remove this override so the plan value applies again.',
      cleared: 'Override removed.',
      override: 'Override',
      overrideHint: 'Set a customer-specific value for this flag.',
      overrideTitle: 'Override for “{{name}}”',
      overrideSaved: 'Override saved.',
    },
    verification: {
      title: 'Verification',
      subtitle: 'When each customer’s media verification is next due, from their plan’s frequency.',
      empty: 'No active subscriptions to schedule.',
      colLast: 'Last verified',
      colDue: 'Due',
      colState: 'State',
      never: 'Never',
      overdue: 'Overdue',
      onTrack: 'On track',
      unschedulable: 'Frequency not recognised',
      everyNDays: 'every {{count}} days',
    },
    subscriptions: {
      title: 'Subscriptions',
      subtitle: 'Every customer’s plan assignment. Select a cohort on one plan family to bulk-migrate them.',
      empty: 'No subscriptions yet.',
      migrateSelected: 'Migrate {{count}} selected to latest version',
      selectRow: 'Select {{name}}',
      actionDone: 'Subscription updated.',
      migrated: 'Selected subscriptions migrated.',
      previewTitle: 'Preview: migrate {{count}} tenant(s)',
      noChanges: 'No changes to preview for the current selection.',
      noFieldChanges: 'No field changes for this tenant — only the plan version pointer moves.',
      droppedOverrides: 'These overrides don’t exist on the target version and will be dropped: {{fields}}',
      colOld: 'Current',
      colNew: 'New',
      confirmMigrate: 'Confirm migration',
    },
    customers: {
      title: 'Customers',
      subtitle: 'All tenants, their plan and bucket usage across the platform.',
      filterPlaceholder: 'Filter by name or ID …',
      empty: 'No customers found.',
      create: 'New organisation',
      createTitle: 'New organisation',
      created: 'Organisation “{{name}}” created.',
      idLabel: 'Organisation ID',
      idHint: 'Permanent key used in storage paths and the OIDC tenant claim. Lowercase letters, digits and hyphens.',
      idRule: 'Only lowercase letters, digits and hyphens are allowed.',
      colPlan: 'Plan',
      colStatus: 'Status',
      colBuckets: 'Buckets',
      nearLimit: 'at {{percent}}% of limit',
      overLimit: 'over limit ({{percent}}%)',
      refused: '{{count}} refused today',
      noPlan: 'No subscription',
    },
    customerDetail: {
      subtitle: 'Effective limits, usage and buckets for this customer.',
      viewAs: 'View as tenant',
      viewAsHint: 'Opens a time-boxed, audit-logged session scoped to this tenant.',
      rename: 'Rename',
      renameTitle: 'Rename organisation',
      renamed: 'Organisation renamed.',
      idFixed: 'ID “{{id}}” stays fixed — it is referenced by archives, audit records and the subscription.',
      editPlan: 'Plan & rates',
      assignPlan: 'Assign plan',
      assigned: 'Subscription updated.',
      effectiveLimits: 'Effective limits',
      colField: 'Field',
      colValue: 'Value',
      colSource: 'Source',
      usage: 'Usage',
      bytesWritten: 'Written (current period)',
      buckets: 'Buckets',
      bucketsEmpty: 'This customer has no buckets yet.',
    },
    plans: {
      title: 'Subscription plans',
      subtitle: 'Plan catalog — editing never changes existing subscribers; every change creates a new version.',
      create: 'New plan',
      createTitle: 'New plan',
      newVersion: 'New version',
      newVersionTitle: 'New version of “{{name}}”',
      created: 'Plan created.',
      versionCreated: 'New version created.',
      empty: 'No plans yet.',
      colVersion: 'Version',
      nameLabel: 'Plan name',
      purchasedHint: 'Volume the customer bought. 0 = no quota agreed.',
      bytesHint: 'Pick an amount and a unit.',
      listHint: 'Comma-separated',
    },
    assign: {
      title: 'Assign subscription',
      planLabel: 'Plan',
      planPlaceholder: 'Choose a plan …',
      overrideWrite: 'Override: write rate limit (GB/day)',
      overrideRead: 'Override: read rate limit (GB/day)',
      overrideHint: 'Leave blank to use the plan default.',
    },
    status: {
      active: 'Active',
      paused: 'Paused',
      cancelled: 'Cancelled',
      actionPause: 'Pause',
      actionCancel: 'Cancel',
      actionReactivate: 'Reactivate',
    },
    source: {
      plan: 'Plan',
      features: 'Features',
      override: 'Override',
    },
    fields: {
      unit: 'Unit',
      resolvesTo: 'equals {{size}} ({{bytes}} bytes)',
      writeSlaHours: 'Max. time to write to glass (hours)',
      purchasedStorageBytes: 'Purchased storage volume',
      minStoragePerArchiveBytes: 'Minimum storage per bucket',
      rateLimitWriteBytesPerDay: 'Write rate limit / day',
      rateLimitReadBytesPerDay: 'Read rate limit / day',
      verificationLevel: 'Verification level',
      verificationFrequency: 'Verification frequency',
      retrievalOptions: 'Retrieval options',
      priceWriteEurPerGb: 'Write price (EUR/GB)',
      priceReadFreeLifetimeCount: 'Free readouts (lifetime)',
      priceReadEurPerReadout: 'Read price per readout (EUR)',
      priceStorageEurPerGbYear: 'Storage price (EUR/GB/year)',
      physicalPackaging: 'Physical packaging',
      ttfbReadingHours: 'Time to first byte (hours)',
      ttfbSiteId: 'Time-to-first-byte site',
      eccProfile: 'Erasure coding profile',
      storageLocationSiteId: 'Storage location',
      minBillableObjectBytes: 'Minimum billable file size',
      minBillableReadBytes: 'Minimum billable read amount',
      mediumRedundancyPercent: 'Medium redundancy (%)',
      writeTriggers: 'Write triggers',
    },
  },
} as const;

export default en;
